import { Context, Controller } from '@hotwired/stimulus';
import { createElement } from 'src/utils/dom-create';
import { STATE_TOKEN_OPERATOR } from './token_operator_modal_controller';
import { STATE_TOKEN_TYPE } from './token_type_modal_controller';
import { STATE_TOKEN_VALUE } from './token_value_modal_controller';
import { Token, TOKEN_DEFAULTS, TokenData } from './tokens';

const MEDIA_TYPE_COMBOBOX = 'text/vnd.kaboom.combobox-options';

export default class TokenController extends Controller {
  public static targets = ['value'];
  public static values = {
    data: Object,
    initialLabel: String,
    resolveUrl: String,
  };

  declare private hasValueTarget: boolean;
  declare private hasResolveUrlValue: boolean;

  declare private initialLabelValue: string;
  declare private resolveUrlValue: string;
  declare private dataValue: TokenData;
  declare private valueTarget: HTMLButtonElement;

  private restoreContent: string | undefined;
  private computedLabel: Element | string | undefined;
  private token!: Token;

  private resolveTimer: NodeJS.Timeout | number | undefined;
  private resolveController: AbortController | undefined;

  constructor(context: Context) {
    super(context);

    this.onResolvedResponse = this.onResolvedResponse.bind(this);
    this.onResolveHtml = this.onResolveHtml.bind(this);
  }

  connect(): void {
    this.token = Token.fromData(this.dataValue, this.initialLabelValue);

    this.restoreContent = this.element.textContent;
    this.element.textContent = '';

    this.element.append(
      createElement(
        'button',
        {
          type: 'button',
          class: 'button --xs --neutral my-[3px] font-normal rounded-r-none',
          'data-action':
            'search-bar#editToken pointerdown->search-bar#editToken:prevent',
          'data-search-bar-id-param': this.token.id,
          'data-search-bar-state-param': STATE_TOKEN_TYPE,
        },
        this.token.typeLabel,
      ),
      createElement(
        'button',
        {
          type: 'button',
          class:
            'button --xs --neutral my-[3px] font-normal rounded-none border-l-0',
          'data-action':
            'search-bar#editToken pointerdown->search-bar#editToken:prevent',
          'data-search-bar-id-param': this.token.id,
          'data-search-bar-state-param': STATE_TOKEN_OPERATOR,
        },
        this.token.operatorLabel,
      ),
      createElement(
        'button',
        {
          type: 'button',
          class: 'button --xs --neutral my-[3px] rounded-none border-l-0',
          'data-action':
            'search-bar#editToken pointerdown->search-bar#editToken:prevent',
          'data-search-bar-id-param': this.token.id,
          'data-search-bar-state-param': STATE_TOKEN_VALUE,
          'data-search-bar--token-target': 'value',
        },
        this.computedLabel ?? this.token.valueNode,
      ),
      createElement(
        'button',
        {
          type: 'button',
          class:
            'button --xs --neutral my-[3px] font-normal rounded-l-none border-l-0',
          'data-action':
            'search-bar#removeToken pointerdown->search-bar#removeToken:prevent',
          'data-search-bar-id-param': this.token.id,
        },
        [
          createElement('span', { class: 'sr-only' }, 'Remove token'),
          createElement(
            'span',
            { class: { role: 'image', 'aria-hidden': true } },
            '×',
          ),
        ],
      ),
    );

    this.computeLabel();
  }

  disconnect(): void {
    this.element.textContent = this.restoreContent || '';
  }

  private computeLabel() {
    const defaults =
      TOKEN_DEFAULTS[this.token.type as keyof typeof TOKEN_DEFAULTS];

    if (defaults) {
      const match = defaults.find(({ value }) => this.token.value === value);
      if (match) {
        this.setComputedLabel(match.label);
        return;
      }
    }

    // Already had a computed label, no need to resolve anything
    if (this.element.getAttribute('data-computed') === 'true') {
      this.computedLabel = this.token.valueNode;
      return;
    }

    clearTimeout(this.resolveTimer);

    this.resolveController?.abort('Resolvable value has changed');
    if ('AbortController' in window) {
      this.resolveController = new AbortController();
    }

    if (!this.hasResolveUrlValue || this.resolveUrlValue === '') {
      return;
    }

    this.loading = true;

    fetch(this.resolveUrlValue, {
      method: 'GET',
      headers: {
        accept: `${MEDIA_TYPE_COMBOBOX}, application/problem+json; q=0.1`,
      },
      signal: this.resolveController?.signal,
    })
      .then(this.onResolvedResponse)
      .then(this.onResolveHtml)
      .catch((error) => {
        this.loading = false;
        console.error('[search-bar] stopped resolve', error);
      });
  }

  private setComputedLabel(computedLabel: Element | string | undefined) {
    this.computedLabel = computedLabel;

    if (!this.hasValueTarget) {
      return;
    }

    switch (typeof computedLabel) {
      case 'undefined': {
        this.valueTarget.textContent = this.token.value;
        break;
      }

      case 'string': {
        this.valueTarget.textContent = computedLabel;
        break;
      }

      default: {
        this.valueTarget.innerHTML = '';
        this.valueTarget.append(computedLabel);
      }
    }
  }

  private async onResolvedResponse(value: Response) {
    const contentType = value.headers.get('content-type') || '';

    if (contentType.startsWith(MEDIA_TYPE_COMBOBOX)) {
      return value.text();
    }

    if (contentType.includes(`variant=${MEDIA_TYPE_COMBOBOX}`)) {
      return value.text();
    }

    throw new Error(`Expected ${MEDIA_TYPE_COMBOBOX}, actual ${contentType}`);
  }

  private onResolveHtml(html: string) {
    this.loading = false;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    const li = wrapper.querySelector('li[role="option"]');
    if (!li) {
      return;
    }

    const label = createElement('div', {
      class: 'flex flex-row w-full items-center max-h-[16px]',
    });
    label.innerHTML = li.innerHTML;
    this.setComputedLabel(label);
  }

  private set loading(next: boolean) {
    // TODO: spinner?
  }
}
